import { type ComponentType, lazy, type LazyExoticComponent } from 'react';

const RETRY_STORAGE_PREFIX = 'lazy-reload:';

/**
 * True for the dynamic-import / chunk-load failures that otherwise unmount the
 * whole React tree to a blank screen:
 *  - Vite dev: a stale HMR `?t=<timestamp>` URL after the module graph moved on
 *    (i.e. the code changed while a tab sat in the background);
 *  - Vite prod: a rotated chunk hash after a deploy — an old tab references a
 *    file that no longer exists;
 *  - `ERR_NETWORK_CHANGED`: a chunk fetch interrupted when a backgrounded tab's
 *    machine sleeps/wakes or the network reconnects.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|chunkloaderror|loading chunk \S+ failed/i.test(
    message,
  );
}

// React.lazy is itself typed as `T extends ComponentType<any>`; the route
// wrappers take no props, so a loosely-typed component is all we need here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyComponent = ComponentType<any>;
type ModuleFactory = () => Promise<{ default: LazyComponent }>;

/**
 * Builds the retrying loader used by {@link lazyWithRetry}. Exported for unit
 * tests.
 *
 * The first chunk-load failure for a module forces ONE full page reload — the
 * same recovery a manual refresh performs, re-fetching index.html with valid
 * module URLs. A sessionStorage flag (cleared on the next success) prevents a
 * reload loop when the module is genuinely broken; in that case the rejection
 * is re-thrown so the route ErrorBoundary can render a recoverable message
 * instead of a blank page. Non-chunk errors are always re-thrown untouched.
 */
export function createRetryingLoader(name: string, factory: ModuleFactory): ModuleFactory {
  const storageKey = `${RETRY_STORAGE_PREFIX}${name}`;
  return async () => {
    try {
      const module = await factory();
      window.sessionStorage.removeItem(storageKey);
      return module;
    } catch (error) {
      if (isChunkLoadError(error) && !window.sessionStorage.getItem(storageKey)) {
        window.sessionStorage.setItem(storageKey, '1');
        window.location.reload();
        // Resolve never: keep <Suspense> on its fallback while the reload
        // navigates away, instead of surfacing the rejection as a render error.
        return new Promise<{ default: LazyComponent }>(() => {});
      }
      throw error;
    }
  };
}

/**
 * Drop-in replacement for React.lazy that survives the dynamic-import failures
 * which otherwise blank the whole app (see {@link isChunkLoadError}).
 *
 * @param name    Stable identifier used to scope the one-shot reload guard.
 * @param factory The same `() => import('...')` you would pass to React.lazy.
 */
export function lazyWithRetry(name: string, factory: ModuleFactory): LazyExoticComponent<LazyComponent> {
  return lazy(createRetryingLoader(name, factory));
}
